import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CloudDownload, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { cachedGet, errMsg } from "@/lib/api";
import { Btn, DISCIPLINES, Loading, Pick, StatusPill, useStageNames, fmtDate } from "@/components/app/bits";

export function useSystems(pid) {
  const [systems, setSystems] = useState([]);
  useEffect(() => { cachedGet(`/projects/${pid}/progress`).then((r) => setSystems(r.data.systems)).catch(() => {}); }, [pid]);
  return systems;
}

export default function ChecksheetsTab({ project }) {
  const sn = useStageNames();
  const systems = useSystems(project.id);
  const [f, setF] = useState({ search: "", system_id: "", stage: "", discipline: "", status: "" });
  const [data, setData] = useState(null);
  const [offline, setOffline] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    const t = setTimeout(() => cachedGet(`/projects/${project.id}/checksheets`, { ...f, limit: 100 })
      .then((r) => { setData(r.data); setOffline(r.offline); }).catch((e) => toast.error(errMsg(e))), 250);
    return () => clearTimeout(t);
  }, [project.id, f]);

  const saveOffline = async () => {
    setSaving(true);
    let n = 0;
    for (const s of data.items.slice(0, 50)) { try { await cachedGet(`/checksheets/${s.id}`); n += 1; } catch { break; } }
    setSaving(false);
    toast.success(`${n} checksheets available offline`);
  };

  return (
    <div className="space-y-4" data-testid="checksheets-tab">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <div className="relative col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-500" />
          <Input value={f.search} onChange={set("search")} placeholder="Search tag or checksheet" className="pl-9 h-9 bg-[#0F172A]" data-testid="checksheet-search" />
        </div>
        <Pick testid="checksheet-filter-system" value={f.system_id} onChange={set("system_id")} allLabel="All systems" options={systems.map((s) => [s.id, `${s.code} ${s.name}`])} />
        <Pick testid="checksheet-filter-stage" value={f.stage} onChange={set("stage")} allLabel="All stages" options={["A", "B", "C"].map((s) => [s, `${s} · ${sn[s]}`])} />
        <Pick testid="checksheet-filter-discipline" value={f.discipline} onChange={set("discipline")} allLabel="All disciplines" options={DISCIPLINES} />
        <Pick testid="checksheet-filter-status" value={f.status} onChange={set("status")} allLabel="All statuses" options={[["open", "Open"], ["in_progress", "In progress"], ["complete", "Complete"]]} />
      </div>
      {!data ? <Loading /> : (
        <>
          <div className="flex items-center justify-between text-xs text-slate-500 font-mono">
            <span data-testid="checksheet-count">{data.total.toLocaleString("en-GB")} checksheets{data.total > data.items.length ? `, showing ${data.items.length}` : ""}{offline && " · offline copy"}</span>
            <Btn variant="ghost" className="!py-1.5 !px-3 text-xs" onClick={saveOffline} disabled={saving || offline} data-testid="save-offline-btn"><CloudDownload className="w-3.5 h-3.5" />{saving ? "Saving…" : "Save list for offline"}</Btn>
          </div>
          <div className="panel divide-y divide-[#1E2D4A]" data-testid="checksheet-list">
            {data.items.map((s) => (
              <Link key={s.id} to={`/app/checksheets/${s.id}`} data-testid={`checksheet-item-${s.id}`} className="flex items-center gap-3 px-4 py-3.5 hover:bg-[#131C31] transition-colors">
                <span className="num text-xs w-6 h-6 flex items-center justify-center border border-[#334155] text-amber-400 shrink-0">{s.stage}</span>
                <div className="flex-1 min-w-0">
                  <p className="num text-sm text-slate-100 truncate">{s.tag_no}</p>
                  <p className="text-xs text-slate-400 truncate">{s.template_code} · {s.template_name} · <span className="capitalize">{s.discipline}</span></p>
                </div>
                <span className="hidden md:block text-xs text-slate-500 num">{s.completed_at ? fmtDate(s.completed_at) : ""}</span>
                <StatusPill status={s.status} />
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </Link>
            ))}
            {!data.items.length && <p className="p-8 text-center text-slate-500 text-sm">No checksheets match these filters.</p>}
          </div>
        </>
      )}
    </div>
  );
}
